import {
  useCallback,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import {
  createApiClient,
  conversationSettingToggleValue,
  type PublicConversation,
  type PublicGroupInvite,
  type GroupInvitePreview,
  type FormerMember,
  type PublicIdentity,
  type SerializedWrappedKey,
  type SystemEvent,
  type EncryptedSystemEventPayload,
  type ConversationSettingsKey,
  type DeleteMessageForEveryoneResponse,
} from '@adieuu/shared';
import { applyThreadSummary } from '../../services/threadSummary';
import { emitConversationThreadEvent } from '../../services/conversationThreadEvents';
import type { MemberSettingsMap } from '../../services/conversationCryptoService';
import { tryEncryptSystemEvent } from '../../services/conversationMetadataService';
import {
  addMemberAction,
  listPendingGroupInvitesAction,
  revokeGroupInviteAction,
  leaveGroupAction,
  promoteToAdminAction,
  removeMemberAction,
  renameGroupAction,
  terminateGroupAction,
  updateMemberSettingsAction,
} from '../../services/conversationGroupActions';
import type { ConversationMessagesState, DecryptedConversation, FetchConversationMessagesFn } from './types';

type ApiClient = ReturnType<typeof createApiClient>;

export interface ConversationGroupInvitesAndDeleteParams {
  api: ApiClient;
  fetchConversations: () => Promise<void>;
  fetchMessagesRef: MutableRefObject<FetchConversationMessagesFn>;
  activeConversationIdRef: MutableRefObject<string | null>;
  setConversations: Dispatch<SetStateAction<DecryptedConversation[]>>;
  setActiveConversationId: Dispatch<SetStateAction<string | null>>;
  activeConversationId: string | null;
  setPendingInvitesRefreshSignal: Dispatch<
    SetStateAction<{ conversationId: string; nonce: number } | null>
  >;
  setInvites: Dispatch<SetStateAction<PublicGroupInvite[]>>;
  setMessagesState: Dispatch<SetStateAction<Record<string, ConversationMessagesState>>>;
  toDecrypted: (conv: PublicConversation) => DecryptedConversation;
  identity: PublicIdentity | null;
  participantProfiles: Record<string, PublicIdentity>;
  prepareConversationMetadata: (
    conversationId: string
  ) => Promise<{ metadataKey: Uint8Array; wrappedMetadataKeys: SerializedWrappedKey[] } | null>;
}

/**
 * Group membership, invites, GIF policy, message deletion, and former-member listing.
 */
export function useConversationGroupInvitesAndDelete(
  params: ConversationGroupInvitesAndDeleteParams
) {
  const {
    api,
    fetchConversations,
    fetchMessagesRef,
    activeConversationIdRef,
    setConversations,
    setActiveConversationId,
    activeConversationId,
    setPendingInvitesRefreshSignal,
    setInvites,
    setMessagesState,
    toDecrypted,
    identity,
    participantProfiles,
    prepareConversationMetadata,
  } = params;

  const invitePreviewCache = useRef<Record<string, GroupInvitePreview>>({});

  const namesFor = useCallback(
    (id: string): { displayName?: string; username?: string } => {
      if (identity?.id === id) {
        return { displayName: identity.displayName, username: identity.username };
      }
      const profile = participantProfiles[id];
      if (!profile) return {};
      return { displayName: profile.displayName, username: profile.username };
    },
    [identity, participantProfiles]
  );

  const buildEvent = useCallback(
    (
      type: string,
      identityId: string,
      actorIdentityId?: string,
      extra?: Pick<SystemEvent, 'setting' | 'value'>,
    ): SystemEvent => {
      const names = namesFor(identityId);
      const event: SystemEvent = {
        type,
        identityId,
        displayName: names.displayName,
        username: names.username,
      };
      if (actorIdentityId) {
        const actor = namesFor(actorIdentityId);
        event.actorIdentityId = actorIdentityId;
        event.actorDisplayName = actor.displayName;
        event.actorUsername = actor.username;
      }
      if (extra?.setting) event.setting = extra.setting;
      if (extra?.value) event.value = extra.value;
      return event;
    },
    [namesFor]
  );

  const encryptEvent = useCallback(
    async (conversationId: string, event: SystemEvent) => {
      try {
        const prepared = await prepareConversationMetadata(conversationId);
        if (!prepared) return undefined;
        return tryEncryptSystemEvent(event, conversationId, prepared.metadataKey);
      } catch {
        return undefined;
      }
    },
    [prepareConversationMetadata]
  );

  const encryptSettingsEvent = useCallback(
    async (
      conversationId: string,
      setting?: ConversationSettingsKey,
      value?: string,
    ): Promise<EncryptedSystemEventPayload | undefined> => {
      if (!identity) return undefined;
      return encryptEvent(
        conversationId,
        buildEvent('conversation_settings_updated', identity.id, identity.id, {
          setting,
          value,
        }),
      );
    },
    [identity, encryptEvent, buildEvent],
  );

  const refreshActiveThread = useCallback(
    (conversationId: string) => {
      if (conversationId === activeConversationIdRef.current) {
        fetchMessagesRef.current(conversationId, undefined, true);
      }
    },
    [activeConversationIdRef, fetchMessagesRef],
  );

  const addMember = useCallback(
    async (
      conversationId: string,
      identityId: string,
    ): Promise<{ ok: boolean; errorCode?: string; message?: string }> => {
      const encrypted = identity
        ? await encryptEvent(conversationId, buildEvent('member_invited', identityId, identity.id))
        : undefined;
      const result = await addMemberAction(api, conversationId, identityId, encrypted);
      if (!result.ok) return result;
      await fetchConversations();
      if (conversationId === activeConversationIdRef.current) {
        fetchMessagesRef.current(conversationId, undefined, true);
      }
      return { ok: true };
    },
    [api, fetchConversations, identity, encryptEvent, buildEvent]
  );

  const removeMember = useCallback(
    async (conversationId: string, identityId: string): Promise<boolean> => {
      const encrypted = identity
        ? await encryptEvent(conversationId, buildEvent('member_removed', identityId, identity.id))
        : undefined;
      const ok = await removeMemberAction(api, conversationId, identityId, encrypted);
      if (!ok) return false;
      await fetchConversations();
      if (conversationId === activeConversationIdRef.current) {
        fetchMessagesRef.current(conversationId, undefined, true);
      }
      return true;
    },
    [api, fetchConversations, identity, encryptEvent, buildEvent]
  );

  const leaveGroup = useCallback(
    async (
      conversationId: string,
      options?: { transferAdminTo?: string; transferStrategy?: 'oldest' | 'most_active' }
    ): Promise<boolean> => {
      const encrypted = identity
        ? await encryptEvent(conversationId, buildEvent('member_left', identity.id))
        : undefined;
      const ok = await leaveGroupAction(api, conversationId, {
        ...options,
        ...(encrypted ?? {}),
      });
      if (!ok) return false;
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
      }
      return true;
    },
    [api, activeConversationId, identity, encryptEvent, buildEvent]
  );

  const promoteToAdmin = useCallback(
    async (conversationId: string, identityId: string): Promise<boolean> => {
      const encrypted = identity
        ? await encryptEvent(conversationId, buildEvent('admin_promoted', identityId, identity.id))
        : undefined;
      const ok = await promoteToAdminAction(api, conversationId, identityId, encrypted);
      if (!ok) return false;
      await fetchConversations();
      if (conversationId === activeConversationIdRef.current) {
        fetchMessagesRef.current(conversationId, undefined, true);
      }
      return true;
    },
    [api, fetchConversations, identity, encryptEvent, buildEvent]
  );

  const onPendingInvitesChanged = useCallback((conversationId: string) => {
    setPendingInvitesRefreshSignal({ conversationId, nonce: Date.now() });
  }, []);

  const listPendingGroupInvites = useCallback(
    async (conversationId: string) => listPendingGroupInvitesAction(api, conversationId),
    [api]
  );

  const revokeGroupInvite = useCallback(
    async (conversationId: string, inviteId: string): Promise<boolean> => {
      const ok = await revokeGroupInviteAction(api, conversationId, inviteId);
      if (ok) {
        setPendingInvitesRefreshSignal({ conversationId, nonce: Date.now() });
      }
      return ok;
    },
    [api]
  );

  const terminateGroup = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const ok = await terminateGroupAction(api, conversationId);
      if (!ok) return false;
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
      }
      return true;
    },
    [api, activeConversationId]
  );

  const renameGroup = useCallback(
    async (conversationId: string, newName: string): Promise<boolean> => {
      const prepared = await prepareConversationMetadata(conversationId);
      if (!prepared) return false;
      const result = await renameGroupAction(
        api,
        conversationId,
        newName,
        prepared.metadataKey,
        prepared.wrappedMetadataKeys,
        identity
          ? {
              type: 'group_renamed',
              identityId: identity.id,
              displayName: identity.displayName,
              username: identity.username,
              actorIdentityId: identity.id,
              actorDisplayName: identity.displayName,
              actorUsername: identity.username,
            }
          : undefined
      );
      if (!result.ok) return false;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? {
                ...c,
                encryptedName: result.encryptedName,
                nameNonce: result.nameNonce,
                wrappedMetadataKeys: result.wrappedMetadataKeys,
                decryptedName: newName,
              }
            : c
        )
      );
      if (conversationId === activeConversationIdRef.current) {
        fetchMessagesRef.current(conversationId, undefined, true);
      }
      return true;
    },
    [api, prepareConversationMetadata, identity]
  );

  const updateConversationMemberSettings = useCallback(
    async (conversationId: string, settings: MemberSettingsMap): Promise<boolean> => {
      const prepared = await prepareConversationMetadata(conversationId);
      if (!prepared) return false;
      const result = await updateMemberSettingsAction(
        api,
        conversationId,
        settings,
        prepared.metadataKey,
        prepared.wrappedMetadataKeys
      );
      if (!result.ok) return false;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? {
                ...c,
                encryptedMemberSettings: result.encryptedMemberSettings,
                memberSettingsNonce: result.memberSettingsNonce,
                wrappedMetadataKeys: result.wrappedMetadataKeys,
                decryptedMemberSettings: settings,
              }
            : c
        )
      );
      return true;
    },
    [api, prepareConversationMetadata]
  );

  const updateGifsDisabled = useCallback(
    async (conversationId: string, gifsDisabled: boolean): Promise<boolean> => {
      const encrypted = await encryptSettingsEvent(
        conversationId,
        'gifs_disabled',
        conversationSettingToggleValue(gifsDisabled),
      );
      const resp = await api.conversations.updateGifsDisabled(
        conversationId,
        gifsDisabled,
        encrypted,
      );
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const updateGifContentFilter = useCallback(
    async (conversationId: string, gifContentFilter: string): Promise<boolean> => {
      const encrypted = await encryptSettingsEvent(
        conversationId,
        'gif_content_filter',
        gifContentFilter,
      );
      const resp = await api.conversations.updateGifContentFilter(
        conversationId,
        gifContentFilter as import('@adieuu/shared').GifContentFilter,
        encrypted,
      );
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const updateCustomEmojisDisabled = useCallback(
    async (conversationId: string, customEmojisDisabled: boolean): Promise<boolean> => {
      const encrypted = await encryptSettingsEvent(
        conversationId,
        'custom_emojis_disabled',
        conversationSettingToggleValue(customEmojisDisabled),
      );
      const resp = await api.conversations.updateCustomEmojisDisabled(
        conversationId,
        customEmojisDisabled,
        encrypted
      );
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const updateMessageSearchCachePolicy = useCallback(
    async (
      conversationId: string,
      disallowPersistentMessageSearchCache: boolean
    ): Promise<boolean> => {
      const encrypted = await encryptSettingsEvent(
        conversationId,
        'persistent_search_cache_disallowed',
        conversationSettingToggleValue(disallowPersistentMessageSearchCache),
      );
      const resp = await api.conversations.updateMessageSearchCachePolicy(
        conversationId,
        disallowPersistentMessageSearchCache,
        encrypted
      );
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const updateAllowSkipModeration = useCallback(
    async (conversationId: string, allowSkipModeration: boolean): Promise<boolean> => {
      const encrypted = await encryptSettingsEvent(
        conversationId,
        'allow_skip_moderation',
        conversationSettingToggleValue(allowSkipModeration),
      );
      const resp = await api.conversations.updateAllowSkipModeration(
        conversationId,
        allowSkipModeration,
        encrypted
      );
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const updateCallSettings = useCallback(
    async (
      conversationId: string,
      settings: { audioCallsDisabled?: boolean; videoCallsDisabled?: boolean; screenshareDisabled?: boolean }
    ): Promise<boolean> => {
      const firstSetting =
        settings.audioCallsDisabled !== undefined
          ? ({
              setting: 'allow_audio_calls' as const,
              value: conversationSettingToggleValue(!settings.audioCallsDisabled),
            })
          : settings.videoCallsDisabled !== undefined
            ? ({
                setting: 'allow_video_calls' as const,
                value: conversationSettingToggleValue(!settings.videoCallsDisabled),
              })
            : settings.screenshareDisabled !== undefined
              ? ({
                  setting: 'allow_screenshare' as const,
                  value: conversationSettingToggleValue(!settings.screenshareDisabled),
                })
              : undefined;
      const encrypted = await encryptSettingsEvent(
        conversationId,
        firstSetting?.setting,
        firstSetting?.value,
      );
      const resp = await api.conversations.updateCallSettings(
        conversationId,
        settings,
        encrypted,
      );
      if (!resp.success || !resp.data?.conversation) return false;
      const updated = toDecrypted(resp.data.conversation);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      refreshActiveThread(conversationId);
      return true;
    },
    [api, toDecrypted, encryptSettingsEvent, refreshActiveThread]
  );

  const pinMessage = useCallback(
    async (conversationId: string, messageId: string): Promise<boolean> => {
      const resp = await api.conversations.pinMessage(conversationId, messageId);
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      return true;
    },
    [api, toDecrypted]
  );

  const unpinMessage = useCallback(
    async (conversationId: string, messageId: string): Promise<boolean> => {
      const resp = await api.conversations.unpinMessage(conversationId, messageId);
      if (!resp.success || !resp.data) return false;
      const updated = toDecrypted(resp.data);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...updated, unreadCount: c.unreadCount } : c
        )
      );
      return true;
    },
    [api, toDecrypted]
  );

  const deleteMessage = useCallback(
    async (conversationId: string, messageId: string, forEveryone: boolean): Promise<boolean> => {
      try {
        const resp = forEveryone
          ? await api.conversations.deleteMessageForEveryone(conversationId, messageId)
          : await api.conversations.deleteMessageForSelf(conversationId, messageId);

        if (resp.success) {
          const thread = forEveryone ? (resp.data as DeleteMessageForEveryoneResponse | undefined) : undefined;
          const rootId = thread?.threadRootId;
          const summary = thread?.threadRootSummary;
          setMessagesState((prev) => {
            const state = prev[conversationId];
            if (!state) return prev;
            return {
              ...prev,
              [conversationId]: {
                ...state,
                messages: state.messages.map((m) => {
                  if (m.id === messageId) {
                    return { ...m, deleted: true, decryptedContent: undefined, ciphertext: undefined };
                  }
                  return rootId && summary !== undefined && m.id === rootId ? applyThreadSummary(m, summary) : m;
                }),
              },
            };
          });
          emitConversationThreadEvent({ kind: 'deleted', conversationId, messageId });
          if (rootId && summary !== undefined) {
            emitConversationThreadEvent({ kind: 'summary', conversationId, rootId, summary });
          }
          if (forEveryone) {
            setConversations((prev) =>
              prev.map((c) =>
                c.id === conversationId
                  ? {
                      ...c,
                      pinnedMessageIds: (c.pinnedMessageIds ?? []).filter((pid) => pid !== messageId),
                    }
                  : c
              )
            );
          }
          return true;
        }
      } catch {
        // Error
      }
      return false;
    },
    [api, setConversations]
  );

  const acceptInvite = useCallback(
    async (inviteId: string): Promise<boolean> => {
      try {
        const resp = await api.conversations.acceptInvite(inviteId);
        if (resp.success) {
          setInvites((prev) => prev.filter((i) => i.id !== inviteId));
          await fetchConversations();
          return true;
        }
      } catch {
        // Error
      }
      return false;
    },
    [api, fetchConversations]
  );

  const declineInvite = useCallback(
    async (inviteId: string): Promise<boolean> => {
      try {
        const resp = await api.conversations.declineInvite(inviteId);
        if (resp.success) {
          setInvites((prev) => prev.filter((i) => i.id !== inviteId));
          return true;
        }
      } catch {
        // Error
      }
      return false;
    },
    [api]
  );

  const getInvitePreview = useCallback(
    async (inviteId: string): Promise<GroupInvitePreview | null> => {
      if (invitePreviewCache.current[inviteId]) {
        return invitePreviewCache.current[inviteId];
      }
      try {
        const resp = await api.conversations.getInvitePreview(inviteId);
        if (resp.data) {
          invitePreviewCache.current[inviteId] = resp.data;
          return resp.data;
        }
      } catch {
        // Error
      }
      return null;
    },
    [api]
  );

  const getFormerMembers = useCallback(
    async (conversationId: string): Promise<FormerMember[]> => {
      try {
        const resp = await api.conversations.getFormerMembers(conversationId);
        if (resp.data) {
          return resp.data;
        }
      } catch {
        // Error
      }
      return [];
    },
    [api]
  );

  return {
    addMember,
    removeMember,
    leaveGroup,
    promoteToAdmin,
    onPendingInvitesChanged,
    listPendingGroupInvites,
    revokeGroupInvite,
    terminateGroup,
    renameGroup,
    updateConversationMemberSettings,
    updateGifsDisabled,
    updateGifContentFilter,
    updateCustomEmojisDisabled,
    updateMessageSearchCachePolicy,
    updateAllowSkipModeration,
    updateCallSettings,
    pinMessage,
    unpinMessage,
    deleteMessage,
    acceptInvite,
    declineInvite,
    getInvitePreview,
    getFormerMembers,
  };
}
